/**
 * Les succès (GDD §12).
 *
 * Ils remplissent deux rôles à la fois : donner des objectifs nommés à un jeu
 * qui n'en avait que d'implicites, et fournir au classement une matière plus
 * intéressante qu'un simple niveau — deux joueurs de niveau 40 n'ont pas
 * forcément vu les mêmes choses.
 *
 * **Ils se mesurent sur l'état, jamais sur un compteur qu'on incrémente.** Un
 * compteur dédié serait une seconde source de vérité à maintenir, et se
 * désynchroniserait au premier changement de règle. Chaque succès est donc une
 * question posée à la sauvegarde : « combien de Bouftous ? », « quel niveau ? ».
 * On peut en ajouter, en retirer, en recalibrer sans migration.
 *
 * Le serveur les recalcule de son côté sur un état **ramené dans les bornes du
 * miroir** (voir `verif.ts`) : un `localStorage` gonflé ne débloque donc rien.
 *
 * Module pur : il tourne dans le Worker comme dans le navigateur.
 */
import type { Etat } from "./etat.ts"
import type { Contenu } from "./contenu.ts"
import { niveauMetier } from "./recolte.ts"
import { panoplies, palierPanoplie } from "./objets.ts"

export type Categorie =
  | "niveau" | "combat" | "famille" | "zone" | "donjon"
  | "equipe" | "metier" | "collection" | "craft" | "fortune"

export interface Succes {
  cle: string
  nom: string
  /** ce qu'il faut faire, à l'infinitif : le joueur lit un objectif */
  detail: string
  categorie: Categorie
  /** valeur à atteindre */
  seuil: number
  /** où en est le joueur */
  mesure: (e: Etat, c: Contenu) => number
  /**
   * Points accordés, quand la racine du seuil ne dit pas la difficulté.
   *
   * « Abattre 100 Chafers » et « abattre 100 Chocomanciens » ont le même seuil
   * et rien à voir : c'est le niveau du contenu qui fait l'exploit.
   */
  points?: number
}

const LIBELLE_CATEGORIE: Record<Categorie, string> = {
  niveau: "Niveau", combat: "Combat", famille: "Familles", zone: "Zones",
  donjon: "Donjons", equipe: "Équipe", metier: "Métiers",
  collection: "Collection", craft: "Atelier", fortune: "Fortune",
}
export const libelleCategorie = (c: Categorie) => LIBELLE_CATEGORIE[c]

// ---------------------------------------------------------------- mesures

const niveauMax = (e: Etat) => e.personnages.reduce((a, p) => Math.max(a, p.niveau), 0)
const killsTotal = (e: Etat) => e.personnages.reduce((a, p) => a + p.kills, 0)
const killsDe = (e: Etat, familleId: number) => (e.killsFamille ?? {})[familleId] ?? 0
const famillesVues = (e: Etat) =>
  Object.values(e.killsFamille ?? {}).filter((n) => n > 0).length
const metiersOuverts = (e: Etat) => {
  const vus = new Set<number>()
  for (const p of e.personnages) {
    for (const [m, v] of Object.entries(p.metiers)) if ((v?.niveau ?? 0) >= 10) vus.add(Number(m))
  }
  return vus.size
}
const meilleurJet = (e: Etat) => Math.max(0, ...Object.values(e.recordsCraft ?? {}))
const jetsParfaits = (e: Etat) => Object.values(e.recordsCraft ?? {}).filter((v) => v >= 100).length
/**
 * Panoplies dont un palier a été actif **au moins une fois**.
 *
 * On ne porte qu'un équipement à la fois : compter celles qui sont actives
 * *en ce moment* rendait « trois panoplies » impossible, puisque changer de
 * panoplie fait retomber le compte à une. Ce qu'on récompense, c'est d'en avoir
 * complété plusieurs au fil du jeu. Le moteur tient le registre (`panopliesVues`),
 * on ne fait qu'y ajouter ce qui est actif à l'instant.
 */
export const panopliesActives = (e: Etat, c: Contenu): number[] => {
  const vues = new Set<number>(e.panopliesVues ?? [])
  for (const p of e.personnages) {
    for (const [set, n] of panoplies(p.equipement, c)) {
      if (palierPanoplie(c.panoplies[String(set)], n)) vues.add(set)
    }
  }
  return [...vues]
}

/** Familles emblématiques, celles dont on parle. */
const BOUFTOUS = 9, LARVES = 11, PIOUS = 33

/**
 * Construit une série de paliers sur une même mesure.
 *
 * **Les points suivent le rang, pas le seuil.** Ils valaient la racine du
 * seuil, ce qui n'avait aucun sens sur des échelles qui vont de 1 à cent
 * millions : « posséder 100 000 000 kamas » pesait dix mille points à lui seul,
 * la Fortune valait plus de la moitié du catalogue, et le classement
 * récompensait un jalon de fortune plutôt que le contenu parcouru. Un rang vaut
 * cinq points de plus que le précédent, quelle que soit l'unité comptée.
 */
export const POINTS_PAR_RANG = 5

function paliers(
  base: string, nom: string, detail: (n: number) => string, categorie: Categorie,
  mesure: Succes["mesure"], seuils: number[],
): Succes[] {
  return seuils.map((seuil, rang) => ({
    cle: `${base}-${seuil}`, nom: `${nom} ${seuil}`, detail: detail(seuil), categorie, seuil, mesure,
    points: POINTS_PAR_RANG * (rang + 1),
  }))
}

/**
 * Le catalogue, **construit depuis le contenu**.
 *
 * Il était écrit à la main : soixante-huit entrées pour cent soixante-dix-huit
 * familles de monstres, quarante zones et six métiers, c'est-à-dire presque
 * rien de ce que le jeu contient. Les succès qui comptent sont ceux qui
 * désignent une chose précise à aller faire — cette famille-là, cette zone-là —
 * et il n'y a que le contenu pour les énumérer.
 *
 * Le calcul est mémorisé : la liste ne dépend que du contenu, qui ne change pas
 * en cours de partie.
 */
let cache: { c: Contenu; liste: Succes[] } | null = null

export function catalogue(c: Contenu): Succes[] {
  if (cache && cache.c === c) return cache.liste
  const liste = construire(c)
  cache = { c, liste }
  return liste
}

/** Points d'un succès de contenu : ce qui est haut niveau vaut plus cher. */
const selonNiveau = (niveau: number, base = 4) => Math.max(base, Math.round(base + niveau / 4))

function construire(c: Contenu): Succes[] {
  const familles = Object.values(c.familles)
  const zones = Object.values(c.zones)
  const donjons = Object.values(c.donjons)

  const parZone = new Map<number, typeof familles>()
  for (const f of familles) {
    if (f.zone == null) continue
    parZone.set(f.zone, [...(parZone.get(f.zone) ?? []), f])
  }
  const donjonsParZone = new Map<number, typeof donjons>()
  for (const d of donjons) {
    if (d.zone == null) continue
    donjonsParZone.set(d.zone, [...(donjonsParZone.get(d.zone) ?? []), d])
  }

  return [
    ...paliers("niveau", "Niveau", (n) => `Amener un personnage au niveau ${n}`, "niveau",
      niveauMax, [10, 25, 50, 75, 100, 150, 200]),

    ...paliers("kills", "Victoires", (n) => `Remporter ${n.toLocaleString("fr-FR")} combats`, "combat",
      killsTotal, [100, 1_000, 10_000, 100_000, 1_000_000]),
    ...paliers("familles", "Bestiaire", (n) => `Affronter ${n} familles de monstres différentes`, "combat",
      famillesVues, [5, 20, 50, 100, familles.length]),
    // Les trois familles emblématiques gardent leurs paliers : ce sont celles
    // dont on parle, et elles jalonnent le début de partie.
    ...paliers("bouftou", "Bouftous", (n) => `Abattre ${n.toLocaleString("fr-FR")} Bouftous`, "combat",
      (e) => killsDe(e, BOUFTOUS), [100, 1_000, 10_000]),
    ...paliers("larve", "Larves", (n) => `Abattre ${n.toLocaleString("fr-FR")} Larves`, "combat",
      (e) => killsDe(e, LARVES), [100, 1_000]),
    ...paliers("piou", "Pious", (n) => `Abattre ${n.toLocaleString("fr-FR")} Pious`, "combat",
      (e) => killsDe(e, PIOUS), [100, 1_000]),

    // Un succès par famille : c'est la liste des choses à aller faire.
    ...familles.map((f): Succes => ({
      cle: `fam-${f.id}`,
      nom: f.nom,
      detail: `Abattre 100 monstres de la famille « ${f.nom} » (niveau ${f.niveauMin}${f.niveauMax !== f.niveauMin ? `–${f.niveauMax}` : ""})`,
      categorie: "famille",
      seuil: 100,
      points: selonNiveau(f.niveauMax),
      mesure: (e) => killsDe(e, f.id),
    })),

    // Une zone entière : toutes ses familles au moins entamées.
    ...[...parZone].map(([zid, fams]): Succes => {
      const z = zones.find((x) => x.id === zid)
      return {
        cle: `zone-${zid}`,
        nom: z?.nom ?? "Zone",
        detail: `Affronter les ${fams.length} famille${fams.length > 1 ? "s" : ""} de ${z?.nom ?? "cette zone"}`,
        categorie: "zone",
        seuil: fams.length,
        points: selonNiveau(z?.niveauMax ?? 50, 8),
        mesure: (e) => fams.filter((f) => killsDe(e, f.id) > 0).length,
      }
    }),

    ...paliers("donjon", "Donjons", (n) => `Vaincre le boss de ${n} donjon${n > 1 ? "s" : ""}`, "donjon",
      (e) => new Set(e.bossVaincus).size, [1, 5, 15, 40, donjons.length]),
    // Et le nettoyage complet d'une zone, donjon par donjon.
    ...[...donjonsParZone].filter(([, l]) => l.length > 1).map(([zid, l]): Succes => {
      const z = zones.find((x) => x.id === zid)
      const ids = new Set(l.flatMap((d) => d.boss))
      return {
        cle: `djzone-${zid}`,
        nom: `Donjons de ${z?.nom ?? "la zone"}`,
        detail: `Vaincre les ${l.length} donjons de ${z?.nom ?? "cette zone"}`,
        categorie: "donjon",
        seuil: l.length,
        points: selonNiveau(z?.niveauMax ?? 50, 12),
        mesure: (e) => new Set(e.bossVaincus.filter((b) => ids.has(b))).size,
      }
    }),

    ...paliers("equipe", "Équipe", (n) => `Compter ${n} personnages`, "equipe",
      (e) => e.personnages.length, [2, 4, 6, 8]),

    // Chaque métier a ses paliers : monter les six est un projet en soi.
    ...Object.entries(c.metiers).flatMap(([id, m]) =>
      [20, 60, 100].map((seuil, rang): Succes => ({
        cle: `metier-${id}-${seuil}`,
        nom: `${m.nom} ${seuil}`,
        detail: `Monter ${m.nom} au niveau ${seuil}`,
        categorie: "metier",
        seuil,
        points: POINTS_PAR_RANG * 2 * (rang + 1),
        mesure: (e) => e.personnages.reduce((a, p) => Math.max(a, niveauMetier(p, Number(id))), 0),
      }))),
    { cle: "metiers-tous", nom: "Polyvalent", detail: "Ouvrir les six métiers au niveau 10",
      categorie: "metier", seuil: 6, points: 60, mesure: metiersOuverts },

    ...paliers("archi", "Archimonstres", (n) => `Capturer ${n} archimonstres`, "collection",
      (e) => e.archimonstres.length, [1, 10, 50, 150, Object.keys(c.archimonstres).length]),
    ...paliers("recherche", "Avis de recherche", (n) => `Vaincre ${n} monstres recherchés`, "collection",
      (e) => e.recherches.length, [1, 10, 40, Object.keys(c.recherches).length]),
    ...paliers("dofus", "Dofus", (n) => `Obtenir ${n} Dofus`, "collection",
      (e) => e.dofus.length, [1, 3, 6]),
    ...paliers("panoplie", "Panoplies", (n) => `Compléter ${n} panoplie${n > 1 ? "s" : ""}`, "collection",
      (e, ct) => panopliesActives(e, ct).length, [1, 3, 10, 25]),

    ...paliers("craft", "Artisan", (n) => `Fabriquer ${n} objets différents`, "craft",
      (e) => Object.keys(e.recordsCraft ?? {}).length, [1, 25, 100, 400, 1000]),
    { cle: "jet-90", nom: "Beau jet", detail: "Obtenir un jet à 90 % ou plus",
      categorie: "craft", seuil: 90, points: 15, mesure: meilleurJet },
    // La forge (GDD §19). Elle se compte en **lignes remontées**, pas en
    // tentatives : payer pour rater n'est pas un accomplissement.
    ...paliers("forge", "Forgeron", (n) => `Remonter ${n.toLocaleString("fr-FR")} ligne${n > 1 ? "s" : ""} à la forge`, "craft",
      (e) => e.forges ?? 0, [1, 50, 500, 5_000, 50_000]),
    // La surpuissance, elle, se compte à l'unité : le cinquième cran au-dessus
    // d'un maximum coûte déjà des dizaines d'heures de jeu.
    ...paliers("surpuissance", "Surpuissance", (n) => `Arracher ${n} cran${n > 1 ? "s" : ""} au-dessus d'un maximum`, "craft",
      (e) => e.surpuissances ?? 0, [1, 5, 25, 100]),
    ...paliers("parfait", "Perfection", (n) => `Réussir ${n} jet${n > 1 ? "s" : ""} parfait${n > 1 ? "s" : ""}`, "craft",
      jetsParfaits, [1, 5, 25, 100]),

    ...paliers("kamas", "Fortune", (n) => `Posséder ${n.toLocaleString("fr-FR")} kamas`, "fortune",
      (e) => e.kamas, [10_000, 100_000, 1_000_000, 10_000_000, 100_000_000]),
    ...paliers("coffre", "Collectionneur", (n) => `Garder ${n} pièces au coffre`, "fortune",
      (e) => e.coffre.length, [25, 100, 300]),
  ]
}

/** Où en est le joueur sur un succès, entre 0 et 1. */
export const avancement = (s: Succes, e: Etat, c: Contenu): number =>
  Math.max(0, Math.min(1, s.mesure(e, c) / s.seuil))

/**
 * Les succès acquis : ceux inscrits dans la partie, **plus** ceux que l'état
 * courant remplit.
 *
 * Un succès ne se reperd jamais. Ils se mesuraient sur l'état seul, ce qui
 * allait tant que la mesure ne pouvait que monter — mais la fortune se dépense
 * et le coffre se vide : atteindre cent mille kamas décrochait le succès, les
 * dépenser le reprenait. Le moteur inscrit l'acquis dans `etat.succes` ; on ne
 * fait ici que réunir les deux.
 */
export function obtenus(e: Etat, c: Contenu): string[] {
  const acquis = new Set(Object.keys(e.succes ?? {}))
  for (const s of catalogue(c)) if (s.mesure(e, c) >= s.seuil) acquis.add(s.cle)
  return [...acquis]
}

/** Ceux que l'état remplit à l'instant et qui ne sont pas encore inscrits. */
export function nouveaux(e: Etat, c: Contenu): string[] {
  const acquis = e.succes ?? {}
  return catalogue(c)
    .filter((s) => acquis[s.cle] === undefined && s.mesure(e, c) >= s.seuil)
    .map((s) => s.cle)
}

/**
 * Points de succès.
 *
 * Deux barèmes, selon ce qui fait la difficulté :
 *
 * - **une série de paliers** vaut son rang — cinq, dix, quinze… — parce que le
 *   seuil lui-même ne dit rien : cent kamas et cent millions de kamas se
 *   comparent mal, et la racine du seuil donnait dix mille points à une seule
 *   ligne ;
 * - **un succès de contenu** — cette famille, cette zone, ce donjon — vaut le
 *   niveau du contenu, parce que c'est là que réside l'exploit : « abattre 100
 *   Chafers » et « abattre 100 Chocomanciens » ont le même seuil et rien à voir.
 *
 * L'effet recherché : le classement récompense **le contenu parcouru**, pas un
 * jalon de fortune atteint une fois.
 */
export const pointsDe = (s: Succes): number => s.points ?? POINTS_PAR_RANG

export function points(cles: string[], c: Contenu): number {
  const parCle = new Map(catalogue(c).map((s) => [s.cle, s]))
  return cles.reduce((a, k) => {
    const s = parCle.get(k)
    return a + (s ? pointsDe(s) : 0)
  }, 0)
}

export const totalPoints = (c: Contenu): number =>
  catalogue(c).reduce((a, s) => a + pointsDe(s), 0)
